/**
 * CommunityPlaybooksTab (DEV-1278) — the community's playbook catalog.
 * Lists the playbooks this community has published (active only) as flat
 * Variation-A cards, and hosts the instantiation modal. This is the
 * consumer/"adopt" surface; authoring/governance (publish, hide, delete)
 * lives under the Management tab.
 *
 * Self-sources community entities/relationships via useApiCommunityData so
 * it needs no prop threading through the route wrappers.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { useApiCommunityData } from "../../api/useApiCommunityData";
import { useApiSecurityData } from "../../api/useApiSecurityData";
import { eligibleRunOrgs } from "../../Data/MiniProjectConfig/playbookEligibility";
import {
  fetchMiniProjectPlaybooks,
  dispatchMiniProjectMutated,
  type MiniProjectPlaybook,
} from "../../api/miniProjects";
import PlaybookCard from "../PlaybookCard/PlaybookCard";
import InstantiatePlaybookModal from "../InstantiatePlaybookModal/InstantiatePlaybookModal";
import styles from "./CommunityPlaybooksTab.module.css";

interface Props {
  communityGuid: string;
  currentUserGuid: string;
  /** True when the viewer can author in this community (owner / admin /
   *  content_creator / sysadmin). Gates the "View master" affordance —
   *  it points at the live, mid-edit master, so consumers never see it. */
  canAuthor?: boolean;
}

export default function CommunityPlaybooksTab({ communityGuid, currentUserGuid, canAuthor = false }: Props) {
  const { t } = useAppTranslation("pages");
  const navigate = useNavigate();
  const { communityEntities, communityRelationships } = useApiCommunityData();
  const { communityRoles, securityUsers } = useApiSecurityData();

  const isSysadmin = useMemo(
    () =>
      (securityUsers || []).some(
        (u: any) =>
          String(u.user_guid || "").toUpperCase() === currentUserGuid.toUpperCase() &&
          !!u.is_system_admin,
      ),
    [securityUsers, currentUserGuid],
  );

  const [playbooks, setPlaybooks] = useState<MiniProjectPlaybook[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<MiniProjectPlaybook | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPlaybooks(null);
    setError(null);
    fetchMiniProjectPlaybooks({ ownerCommunityGuid: communityGuid })
      .then((rows) => {
        if (!cancelled) setPlaybooks(rows);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [communityGuid]);

  const onViewMaster = useCallback(
    (masterGuid: string) => navigate(`/projects/${masterGuid}/about`),
    [navigate],
  );

  // DEV-1282 (Skip, 2026-06-09): show a playbook only if the viewer can run
  // it somewhere — never surface a playbook a user can't create a project
  // from. Hidden everywhere, including this community's own tab.
  const visiblePlaybooks = useMemo(
    () =>
      (playbooks || []).filter(
        (pb) =>
          eligibleRunOrgs(pb, currentUserGuid, communityEntities, communityRelationships, communityRoles || [], isSysadmin)
            .length > 0,
      ),
    [playbooks, currentUserGuid, communityEntities, communityRelationships, communityRoles, isSysadmin],
  );

  return (
    <div className={styles.wrap}>
      <div className={styles.header}>
        <div className={styles.intro}>{t("playbooksTab.intro")}</div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {!playbooks && !error && <div className={styles.muted}>{t("playbooksTab.loading")}</div>}

      {playbooks && visiblePlaybooks.length === 0 && (
        <div className={styles.empty}>{t("playbooksTab.empty")}</div>
      )}

      {visiblePlaybooks.length > 0 && (
        <div className={styles.grid}>
          {visiblePlaybooks.map((pb) => (
            <PlaybookCard
              key={pb.playbookGuid}
              playbook={pb}
              onUse={setSelected}
              onViewMaster={onViewMaster}
              showViewMaster={
                canAuthor ||
                (!!pb.ownerUserGuid &&
                  pb.ownerUserGuid.toUpperCase() === currentUserGuid.toUpperCase())
              }
            />
          ))}
        </div>
      )}

      {selected && (
        <InstantiatePlaybookModal
          playbook={selected}
          currentUserGuid={currentUserGuid}
          communities={communityEntities}
          relationships={communityRelationships}
          onClose={() => setSelected(null)}
          onCreated={(newGuid) => {
            setSelected(null);
            dispatchMiniProjectMutated(newGuid);
            navigate(`/projects/${newGuid}`);
          }}
        />
      )}
    </div>
  );
}
